import { getCustomTag, sendNotification, useTranslation } from '@msdk/common'
import { DefaultActionsDetails, DefaultActionType, EntityOperation } from '@msdk/definitions'
import { ButtonBase, ButtonIcon, IconSelector, styled, Tooltip } from '@msdk/ui'
import React from 'react'

import { useComponentModal } from '../../hooks'
import { messagesFromEntityOperation } from '../../utils'
import { ActionConfirmationModal } from '../ActionConfirmationModal'
import { ActionLabels, getDectivateActionLabels } from '../DynamicView/RowActions/labels-utils'
import { ActionVisualizationMode } from './action-visualization'
import { ActionMenuItem, ActionMenuText } from './ActionMenuItem'

const MoveToBinButton = styled(ButtonBase)`
  border: 0;
  text-transform: none;
`

const StyledButtonIcon = styled(ButtonIcon)`
  padding: 4px;
  color: ${({ theme }) => theme.palette.primary.main};
`

interface Props {
  onMoveToBin: () => Promise<EntityOperation>
  visualizationMode: ActionVisualizationMode
  onClose?: () => void
  actionDetails?: DefaultActionsDetails
}

export function MoveToBin(props: Props) {
  const t = useTranslation()
  const { createModal } = useComponentModal()

  const labels: ActionLabels = getDectivateActionLabels(props.actionDetails, t)

  const handleClick = async () => {
    props.onClose && props.onClose()

    const onConfirm = async () => {
      const res = await props.onMoveToBin()

      const serverMessages = messagesFromEntityOperation(res)
      if (serverMessages.length) {
        const translated = serverMessages.reduce((acc, curr) => (acc += `\n${t(curr.idml, curr.description)}`), '')
        sendNotification({ message: translated, status: 'error' })
      } else {
        if (res) {
          sendNotification({ message: labels.success, status: 'success' })
        } else {
          sendNotification({ message: labels.error, status: 'error' })
        }
      }
    }

    const modalRef = createModal(
      <ActionConfirmationModal
        title={labels.title}
        body={labels.body}
        onConfirm={onConfirm}
        confirmationLabel={labels.confirmationLabel}
        type={DefaultActionType.Deactivate}
      />,
      () => {
        //do nothing
      },
    )

    modalRef.open()
  }

  return (
    <>
      {props.visualizationMode === 'SingleAction' && (
        <Tooltip title={labels.description} arrow>
          <StyledButtonIcon onClick={handleClick}>
            {labels.iconTag && <IconSelector iconName={labels.iconTag} />}
            {!labels.iconTag && labels.description}
          </StyledButtonIcon>
        </Tooltip>
      )}
      {props.visualizationMode === 'Buttons' && (
        <MoveToBinButton
          variant="outlined"
          onClick={handleClick}
          key="MOVE_TO_BIN"
          data-test-id={getCustomTag(['FORM', 'MOVE_TO_BIN', 'button'])}
        >
          {labels.description}
        </MoveToBinButton>
      )}
      {props.visualizationMode === 'MenuItems' && (
        <ActionMenuItem onClick={handleClick} data-test-id={getCustomTag(['FORM', 'MOVE_TO_BIN', 'button'])}>
          <IconSelector iconName={labels.iconTag} />
          <ActionMenuText>{labels.description}</ActionMenuText>
        </ActionMenuItem>
      )}
    </>
  )
}
